"use client";

import { useEffect, useState, type ComponentType } from "react";
import type { HotspotScore, SignalEvent, Tick } from "@/lib/types";
import { copyText } from "@/lib/clipboard";
import { PriceChart } from "./PriceChart";

interface TokenDetail {
  token: {
    id: string;
    symbol: string;
    kind: string;
    venue: string;
    chain?: string | null;
    address?: string | null;
    marketCap?: number | null;
    fdv?: number | null;
    liquidityUsd?: number | null;
    volume24h?: number | null;
    poolCreatedAt?: number | null;
    dex?: string | null;
    pairAddress?: string | null;
  };
  ticks: Tick[];
  events: SignalEvent[];
}

const EXPLORER: Record<string, (addr: string) => string> = {
  ethereum: (a) => `https://etherscan.io/token/${a}`,
  eth: (a) => `https://etherscan.io/token/${a}`,
  bsc: (a) => `https://bscscan.com/token/${a}`,
  base: (a) => `https://basescan.org/token/${a}`,
  arbitrum: (a) => `https://arbiscan.io/token/${a}`,
  polygon: (a) => `https://polygonscan.com/token/${a}`,
  polygon_pos: (a) => `https://polygonscan.com/token/${a}`,
  avalanche: (a) => `https://snowtrace.io/token/${a}`,
  solana: (a) => `https://solscan.io/token/${a}`,
  sui: (a) => `https://suiscan.xyz/mainnet/object/${a}`,
  ton: (a) => `https://tonscan.org/address/${a}`,
  hyperevm: (a) => `https://hyperliquid.xyz/address/${a}`,
};

function explorerUrl(chain: string | null | undefined, address: string): string | null {
  if (!chain) return null;
  const fn = EXPLORER[chain.toLowerCase()];
  return fn ? fn(address) : null;
}

function formatMoney(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v) || v <= 0) return "—";
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${v.toFixed(0)}`;
}

interface Props {
  score: HotspotScore;
  onClose: () => void;
  SignalBar: ComponentType<{ label: string; value: number }>;
}

function formatRel(ts: number): string {
  const diff = Date.now() - ts;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  return `${Math.floor(hr / 24)}d`;
}

export function TokenDrawer({ score, onClose, SignalBar }: Props) {
  const [detail, setDetail] = useState<TokenDetail | null>(null);
  const [copyStatus, setCopyStatus] = useState<"ok" | "err" | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetch(`/api/token/${encodeURIComponent(score.tokenId)}`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled) setDetail(d as TokenDetail | null);
      });
    return () => {
      cancelled = true;
    };
  }, [score.tokenId]);

  const priceLine = detail?.ticks?.length
    ? (() => {
        const first = detail.ticks[0];
        const last = detail.ticks[detail.ticks.length - 1];
        const pct = ((last.price - first.price) / first.price) * 100;
        return { price: last.price, pct, sample: detail.ticks.length };
      })()
    : null;

  return (
    <div className="fixed inset-0 z-30 bg-black/60" onClick={onClose}>
      <aside
        onClick={(e) => e.stopPropagation()}
        className="absolute right-0 top-0 h-full w-full max-w-xl bg-zinc-950 border-l border-zinc-800 overflow-y-auto"
      >
        <div className="sticky top-0 bg-zinc-950 border-b border-zinc-800 px-6 py-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold">
              {score.kind === "cex-swap" ? (
                <a
                  href={`https://www.binance.com/zh-TW/futures/${score.symbol}USDT`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-amber-300 underline decoration-dotted decoration-zinc-700 underline-offset-4"
                  title="開 Binance 合約頁面（繁中）"
                >
                  {score.symbol} ↗
                </a>
              ) : (
                score.symbol
              )}
            </h2>
            <p className="text-xs text-zinc-500">
              {score.venue} · {score.kind} · hotness {score.hotness.toFixed(2)}
            </p>
            {priceLine && (
              <p className="text-sm mt-1 tabular-nums">
                ${priceLine.price.toPrecision(6)}{" "}
                <span
                  className={priceLine.pct >= 0 ? "text-emerald-400" : "text-rose-400"}
                >
                  {priceLine.pct >= 0 ? "+" : ""}
                  {priceLine.pct.toFixed(2)}%
                </span>
                <span className="text-zinc-600 ml-2 text-xs">
                  近 {priceLine.sample} 個樣本
                </span>
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-500 hover:text-zinc-200 text-xl leading-none"
          >
            ×
          </button>
        </div>

        <section className="px-6 py-4 border-b border-zinc-800">
          <h3 className="text-xs uppercase text-zinc-500 mb-3">近期價格走勢</h3>
          <PriceChart ticks={detail?.ticks ?? []} height={140} />
        </section>

        {detail?.token && (detail.token.chain || detail.token.address) && (
          <section className="px-6 py-4 border-b border-zinc-800 text-sm">
            <h3 className="text-xs uppercase text-zinc-500 mb-3">鏈上資訊</h3>
            <dl className="grid grid-cols-2 gap-y-2 gap-x-4 text-xs">
              <dt className="text-zinc-500">鏈</dt>
              <dd className="text-zinc-200">{detail.token.chain ?? "—"}</dd>
              <dt className="text-zinc-500">合約地址</dt>
              <dd className="font-mono text-zinc-300 break-all">
                {detail.token.address ? (
                  <>
                    {detail.token.address}
                    <button
                      type="button"
                      onClick={async () => {
                        if (!detail.token.address) return;
                        const ok = await copyText(detail.token.address);
                        setCopyStatus(ok ? "ok" : "err");
                        setTimeout(() => setCopyStatus(null), 1500);
                      }}
                      className={
                        "ml-2 " + (copyStatus === "ok"
                          ? "text-emerald-300"
                          : copyStatus === "err"
                            ? "text-rose-400"
                            : "text-zinc-500 hover:text-emerald-300")
                      }
                      title={
                        copyStatus === "ok"
                          ? "已複製"
                          : copyStatus === "err"
                            ? "複製失敗"
                            : "複製"
                      }
                    >
                      {copyStatus === "ok" ? "✓" : copyStatus === "err" ? "✗" : "⎘"}
                    </button>
                    {explorerUrl(detail.token.chain, detail.token.address) && (
                      <a
                        href={explorerUrl(detail.token.chain, detail.token.address) ?? "#"}
                        target="_blank"
                        rel="noreferrer"
                        className="ml-1 text-zinc-500 hover:text-emerald-300"
                      >
                        ↗
                      </a>
                    )}
                  </>
                ) : (
                  "—"
                )}
              </dd>
              <dt className="text-zinc-500">市值 / FDV</dt>
              <dd className="text-zinc-200 tabular-nums">
                {formatMoney(detail.token.marketCap)} / {formatMoney(detail.token.fdv)}
              </dd>
              <dt className="text-zinc-500">24h 成交量</dt>
              <dd className="text-zinc-200 tabular-nums">
                {formatMoney(detail.token.volume24h)}
              </dd>
              <dt className="text-zinc-500">流動性</dt>
              <dd className="text-zinc-200 tabular-nums">
                {formatMoney(detail.token.liquidityUsd)}
              </dd>
              {detail.token.dex && (
                <>
                  <dt className="text-zinc-500">主要 DEX</dt>
                  <dd className="text-zinc-200">{detail.token.dex}</dd>
                </>
              )}
              {detail.token.poolCreatedAt ? (
                <>
                  <dt className="text-zinc-500">池子建立</dt>
                  <dd className="text-zinc-200">
                    {new Date(detail.token.poolCreatedAt).toLocaleString()}
                  </dd>
                </>
              ) : null}
            </dl>
          </section>
        )}

        <section className="px-6 py-4 border-b border-zinc-800">
          <h3 className="text-xs uppercase text-zinc-500 mb-3">信號強度（z-score）</h3>
          <div className="space-y-2">
            <SignalBar label="突破 24h 高" value={score.breakdown.breakout} />
            <SignalBar label="短線加速度" value={score.breakdown.velocity} />
            <SignalBar label="OI 增長" value={score.breakdown.oiGrowth} />
            <SignalBar label="價格動能" value={score.breakdown.priceMomentum} />
            <SignalBar label="成交量異常" value={score.breakdown.volumeSpike} />
            <SignalBar label="消息面" value={score.breakdown.news} />
            <SignalBar label="社群聲量" value={score.breakdown.socialBuzz} />
            <SignalBar label="爆倉密度" value={score.breakdown.liquidations} />
            <SignalBar label="資金費率偏離" value={score.breakdown.fundingBias} />
          </div>
        </section>

        <section className="px-6 py-4">
          <h3 className="text-xs uppercase text-zinc-500 mb-3">
            近期事件 {detail ? `(${detail.events.length})` : ""}
          </h3>
          {!detail && <p className="text-xs text-zinc-600">載入中…</p>}
          {detail && detail.events.length === 0 && (
            <p className="text-xs text-zinc-600">近 6 小時無事件</p>
          )}
          <ul className="space-y-2">
            {detail?.events.slice(0, 30).map((e, i) => (
              <li
                key={`${e.ts}-${i}`}
                className="rounded border border-zinc-900 bg-zinc-900/40 px-3 py-2 text-xs"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] uppercase text-zinc-500">
                    {e.kind} · {e.source}
                  </span>
                  <span className="text-zinc-600 tabular-nums">{formatRel(e.ts)}</span>
                </div>
                {e.url ? (
                  <a
                    href={e.url}
                    target="_blank"
                    rel="noreferrer"
                    className="block mt-1 text-zinc-300 hover:text-emerald-300 break-words"
                  >
                    {e.title}
                  </a>
                ) : (
                  <p className="mt-1 text-zinc-300 break-words">{e.title}</p>
                )}
              </li>
            ))}
          </ul>
        </section>
      </aside>
    </div>
  );
}
